import { useEffect, useState } from 'react';
import { API_BASE } from '../utils/apiClient';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { usePermissions } from 'react-admin';
import ArticleIcon from '@mui/icons-material/Article';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingIcon from '@mui/icons-material/Pending';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';

interface DashboardStats {
  entities: {
    byType: Array<{
      entityType: string;
      total: number;
      verified: number;
      pendingReview: number;
      unverified: number;
      verificationProgress: number;
    }>;
    totals: {
      total: number;
      verified: number;
      pending_review: number;
      unverified: number;
      verificationProgress: number;
    };
  };
  gameConfigs: {
    total: number;
    upcoming: number;
    thisWeek: number;
  };
  adminUsers: number;
}

interface VerificationProgress {
  byEntityType: Array<{
    entityType: string;
    total: number;
    verified: number;
    pendingReview: number;
    unverified: number;
    progress: number;
    categories: Array<{
      category: string | null;
      total: number;
      verified: number;
      pendingReview: number;
      unverified: number;
      progress: number;
    }>;
  }>;
}

interface ActivityEntry {
  id: string;
  userId: string;
  userEmail: string;
  userName: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  createdAt: string;
}

interface DashboardCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const DashboardCard = ({ title, value, icon, color, subtitle }: DashboardCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: '50%',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const VerificationProgressBar = ({
  label,
  verified,
  pendingReview,
  unverified,
  total,
}: {
  label: string;
  verified: number;
  pendingReview: number;
  unverified: number;
  total: number;
}) => {
  const verifiedPercent = total > 0 ? (verified / total) * 100 : 0;
  const pendingPercent = total > 0 ? (pendingReview / total) * 100 : 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {label.replace('_', ' ')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {verified}/{total} verified ({Math.round(verifiedPercent)}%)
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200' }}>
        <Box sx={{ width: `${verifiedPercent}%`, bgcolor: 'success.main' }} />
        <Box sx={{ width: `${pendingPercent}%`, bgcolor: 'warning.main' }} />
      </Box>
      <Box display="flex" gap={1} mt={0.5}>
        <Chip label={`${verified} verified`} size="small" color="success" variant="outlined" />
        <Chip label={`${pendingReview} pending`} size="small" color="warning" variant="outlined" />
        <Chip label={`${unverified} unverified`} size="small" variant="outlined" />
      </Box>
    </Box>
  );
};

const formatAction = (action: string): string => {
  return action
    .replace(/_/g, ' ')
    .replace(/:/g, ': ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

export const Dashboard = () => {
  const { permissions } = usePermissions();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [verificationProgress, setVerificationProgress] = useState<VerificationProgress | null>(null);
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch stats
        const statsResponse = await fetch(`${API_BASE}/admin/api/dashboard/stats`, {
          credentials: 'include',
        });
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData);
        }

        // Fetch verification progress
        const progressResponse = await fetch(`${API_BASE}/admin/api/dashboard/verification-progress`, {
          credentials: 'include',
        });
        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          setVerificationProgress(progressData);
        }

        // Fetch recent activity (if user has permission)
        if (permissions?.role === 'admin' || permissions?.role === 'super_admin') {
          const activityResponse = await fetch(`${API_BASE}/admin/api/dashboard/activity?limit=10`, {
            credentials: 'include',
          });
          if (activityResponse.ok) {
            const activityData = await activityResponse.json();
            setActivities(activityData.activities || []);
          }
        }
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [permissions]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
        Welcome to the Garuda Admin Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardCard
            title="Total Entities"
            value={stats?.entities.totals.total || 0}
            icon={<ArticleIcon sx={{ color: 'white', fontSize: 32 }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardCard
            title="Verified Entities"
            value={stats?.entities.totals.verified || 0}
            subtitle={`${stats?.entities.totals.verificationProgress || 0}% complete`}
            icon={<VerifiedIcon sx={{ color: 'white', fontSize: 32 }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardCard
            title="Pending Review"
            value={stats?.entities.totals.pending_review || 0}
            icon={<PendingIcon sx={{ color: 'white', fontSize: 32 }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardCard
            title="Game Configs"
            value={stats?.gameConfigs.total || 0}
            subtitle={`${stats?.gameConfigs.upcoming || 0} upcoming`}
            icon={<SportsEsportsIcon sx={{ color: 'white', fontSize: 32 }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Verification Progress */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Verification Progress"
              avatar={<VerifiedIcon color="success" />}
            />
            <CardContent>
              {verificationProgress?.byEntityType.map((entityType) => (
                <Box key={entityType.entityType} sx={{ mb: 3 }}>
                  <VerificationProgressBar
                    label={entityType.entityType}
                    verified={entityType.verified}
                    pendingReview={entityType.pendingReview}
                    unverified={entityType.unverified}
                    total={entityType.total}
                  />
                  {entityType.categories.length > 1 && (
                    <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'grey.300' }}>
                      {entityType.categories.map((cat) => (
                        <Box key={cat.category || 'uncategorized'} sx={{ mb: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="textSecondary">
                              {cat.category || 'Uncategorized'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {cat.verified}/{cat.total} ({cat.progress}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={cat.progress}
                            sx={{ height: 4, borderRadius: 1 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats & Activity */}
        <Grid item xs={12} md={4}>
          {/* Game Stats */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title="Game Overview"
              avatar={<ScheduleIcon color="primary" />}
            />
            <CardContent>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">This Week</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats?.gameConfigs.thisWeek || 0} configs
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Upcoming</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats?.gameConfigs.upcoming || 0} games
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Admin Users</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats?.adminUsers || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          {(permissions?.role === 'admin' || permissions?.role === 'super_admin') && (
            <Card>
              <CardHeader
                title="Recent Activity"
                avatar={<HistoryIcon color="action" />}
              />
              <CardContent sx={{ p: 0 }}>
                {activities.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      No recent activity
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {activities.slice(0, 5).map((activity, index) => (
                      <Box key={activity.id}>
                        {index > 0 && <Divider />}
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                              {(activity.userName || activity.userEmail)?.[0]?.toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={formatAction(activity.action)}
                            secondary={
                              <>
                                {activity.userName || activity.userEmail}
                                {' · '}
                                {formatTimeAgo(activity.createdAt)}
                              </>
                            }
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      </Box>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Your Role Card */}
      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <PeopleIcon color="action" />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Your Role
                </Typography>
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  {permissions?.role?.replace('_', ' ') || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
